import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogTrigger,
  DialogClose,
} from '@/components/ui/dialog';
import { Trash2, Pencil, Plus } from 'lucide-react';
import { format } from 'date-fns';

const SLUG_REGEX = /^[a-z][a-z0-9_-]*$/;

export function DomainsPage(): JSX.Element {
  const qc = useQueryClient();
  const query = useQuery({ queryKey: ['domains'], queryFn: () => api.listDomains() });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['domains'] });

  return (
    <div className="space-y-4 max-w-4xl">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight">Domains</h2>
          <p className="text-sm text-muted-foreground">
            Labels for grouping and filtering tasks. Built-in domains can't be deleted.
          </p>
        </div>
        <CreateDomainDialog onCreated={invalidate} />
      </div>

      <Card>
        {query.isLoading && <div className="p-6 text-sm text-muted-foreground">Loading…</div>}
        {query.data?.domains.length === 0 && (
          <div className="p-6 text-sm text-muted-foreground">No domains yet.</div>
        )}
        <ul className="divide-y">
          {query.data?.domains.map((d) => (
            <li key={d.slug} className="px-4 py-3 flex items-start gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{d.name}</span>
                  <code className="text-xs bg-muted px-1.5 py-0.5 rounded">{d.slug}</code>
                  {d.built_in && <Badge variant="outline">built-in</Badge>}
                </div>
                {d.description && (
                  <p className="text-sm text-muted-foreground mt-0.5">{d.description}</p>
                )}
                <p className="text-xs text-muted-foreground mt-1">
                  Created {format(new Date(d.created_at), 'PPp')}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <EditDomainDialog domain={d} onSaved={invalidate} />
                {!d.built_in && <DeleteDomainButton slug={d.slug} onDeleted={invalidate} />}
              </div>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------

function CreateDomainDialog({ onCreated }: { onCreated: () => void }): JSX.Element {
  const [open, setOpen] = useState(false);
  const [slug, setSlug] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const create = useMutation({
    mutationFn: () => api.createDomain({ slug, name, description }),
    onSuccess: () => {
      setOpen(false);
      setSlug('');
      setName('');
      setDescription('');
      onCreated();
    },
  });

  // Auto-derive slug from name on first edit only.
  const [slugTouched, setSlugTouched] = useState(false);
  const handleNameChange = (v: string) => {
    setName(v);
    if (!slugTouched) {
      setSlug(
        v
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '')
          .slice(0, 40),
      );
    }
  };

  const slugValid = slug === '' || SLUG_REGEX.test(slug);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="h-4 w-4 mr-1.5" />
          New domain
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New domain</DialogTitle>
          <DialogDescription>
            Adds a domain to the picker. Slug is the stable identifier; name is what shows in
            the UI.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="domain-name">Name</Label>
            <Input
              id="domain-name"
              value={name}
              onChange={(e) => handleNameChange(e.target.value)}
              placeholder="e.g. Coding"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="domain-slug">Slug</Label>
            <Input
              id="domain-slug"
              value={slug}
              onChange={(e) => {
                setSlugTouched(true);
                setSlug(e.target.value);
              }}
              placeholder="e.g. coding"
            />
            {!slugValid && (
              <p className="text-xs text-destructive">
                Use lowercase letters, digits, hyphens, or underscores
              </p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="domain-description">Description (optional)</Label>
            <Textarea
              id="domain-description"
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
        </div>
        {create.isError && (
          <p className="text-sm text-destructive">Failed: {String(create.error)}</p>
        )}
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="ghost" disabled={create.isPending}>
              Cancel
            </Button>
          </DialogClose>
          <Button
            disabled={!slug || !name || !slugValid || create.isPending}
            onClick={() => create.mutate()}
          >
            {create.isPending ? 'Creating…' : 'Create domain'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------

function EditDomainDialog({
  domain,
  onSaved,
}: {
  domain: { slug: string; name: string; description: string };
  onSaved: () => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(domain.name);
  const [description, setDescription] = useState(domain.description);

  const save = useMutation({
    mutationFn: () => api.editDomain(domain.slug, { name, description }),
    onSuccess: () => {
      setOpen(false);
      onSaved();
    },
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (v) {
          setName(domain.name);
          setDescription(domain.description);
        }
      }}
    >
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" aria-label="Edit">
          <Pencil className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit domain</DialogTitle>
          <DialogDescription>
            <span className="block">
              Slug{' '}
              <code className="text-xs bg-muted px-1.5 py-0.5 rounded">{domain.slug}</code> can't
              be changed. To rename, create a new domain and reassign tasks.
            </span>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="edit-name">Name</Label>
            <Input id="edit-name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="edit-desc">Description</Label>
            <Textarea
              id="edit-desc"
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
        </div>
        {save.isError && (
          <p className="text-sm text-destructive">Failed: {String(save.error)}</p>
        )}
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="ghost" disabled={save.isPending}>
              Cancel
            </Button>
          </DialogClose>
          <Button disabled={!name || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------

function DeleteDomainButton({
  slug,
  onDeleted,
}: {
  slug: string;
  onDeleted: () => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const del = useMutation({
    mutationFn: () => api.deleteDomain(slug),
    onSuccess: () => {
      setOpen(false);
      onDeleted();
    },
  });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" aria-label="Delete">
          <Trash2 className="h-4 w-4 text-destructive" />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete domain?</DialogTitle>
          <DialogDescription>
            <span className="block">
              Removes <code className="text-xs bg-muted px-1.5 py-0.5 rounded">{slug}</code> from
              the picker. Existing tasks tagged with this domain keep their label — they just
              won't appear as a preset choice in the picker anymore.
            </span>
          </DialogDescription>
        </DialogHeader>
        {del.isError && (
          <p className="text-sm text-destructive">Failed: {String(del.error)}</p>
        )}
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="ghost" disabled={del.isPending}>
              Cancel
            </Button>
          </DialogClose>
          <Button variant="destructive" disabled={del.isPending} onClick={() => del.mutate()}>
            {del.isPending ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
